import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GetWasteShipmentQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";

import { ManifestUploadDialog } from "../Manifests/ManifestUploadDialog";
import {
  ATTACH_SHIPMENT_MANIFEST_MUTATION,
  DETACH_SHIPMENT_MANIFEST_MUTATION,
  MANIFEST_LINK_REFETCH_QUERIES,
} from "./api";
import { AttachManifestDialog } from "./AttachManifestDialog";
import { DetachManifestDialog } from "./DetachManifestDialog";
import { ShipmentManifestRailCard } from "./ShipmentDetailChrome";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/**
 * The official manifest for a shipment, and how it compares to what was
 * requested.
 *
 * Attaching is independent of the shipment's own progress — the vendor's copy
 * can arrive before or after the load leaves — so this section is live in every
 * status except canceled, and never gates or is gated by "Mark shipped".
 *
 * A difference here is not an error. The vendor re-weighs at the scale,
 * re-classifies, splits a line; the manifest is the regulated record and the
 * request was an estimate. So the panel reports rather than blocks, and the
 * severity comes from the same scale the manifest discrepancy views use.
 */
export function ShipmentManifestRail({ shipment }: { shipment: Shipment }) {
  const { tenantId } = useTenant();
  const alerts = useAlerts();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detachOpen, setDetachOpen] = useState(false);

  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });
  const [detach, { loading: detaching }] = useMutation(DETACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });

  const reconciliation = shipment.reconciliation;
  const trackingNumber =
    reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;

  const handleSignedChange = async (signed: boolean) => {
    const manifestId = shipment.manifest?.id;
    if (!manifestId) {
      return;
    }
    try {
      // Re-attaching the manifest already linked is the signed-copy flow; the
      // server keeps the original received date.
      await attach({
        variables: { input: { shipmentId: shipment.id, processedManifestId: manifestId, signed } },
      });
      alerts.success(signed ? "Marked as the signed copy." : "Marked as an unsigned draft.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the manifest.");
    }
  };

  const handleDetach = async () => {
    try {
      await detach({ variables: { shipmentId: shipment.id } });
      setDetachOpen(false);
      alerts.success("Manifest detached.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to detach the manifest.");
    }
  };

  return (
    <>
      <ShipmentManifestRailCard
        shipment={shipment}
        attaching={attaching}
        manifestHref={
          tenantId && shipment.manifest
            ? `/o/${tenantId}/waste/manifests/${shipment.manifest.id}`
            : null
        }
        onLink={() => {
          setReplacing(false);
          setPickerOpen(true);
        }}
        onUpload={() => setUploadOpen(true)}
        onRelink={() => {
          setReplacing(true);
          setPickerOpen(true);
        }}
        onDetach={() => setDetachOpen(true)}
        onSignedChange={handleSignedChange}
      />

      {pickerOpen && (
        <AttachManifestDialog
          shipmentId={shipment.id}
          facilityId={shipment.facilityId}
          replacingTrackingNumber={replacing ? trackingNumber : null}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {uploadOpen && tenantId && (
        <ManifestUploadDialog
          open
          onClose={() => setUploadOpen(false)}
          tenantId={tenantId}
          facilityId={shipment.facilityId}
        />
      )}
      {detachOpen && (
        <DetachManifestDialog
          trackingNumber={trackingNumber}
          detaching={detaching}
          onConfirm={handleDetach}
          onClose={() => setDetachOpen(false)}
        />
      )}
    </>
  );
}
